import {
  BackgroundTaskType,
  ProgressApiLatestStatusResponse,
} from "@api/progress-api/progress-api-types";
import { SdbBackgroundTask } from "@custom-types/sdb-background-tasks-types";
import { ISOTimeString } from "@faro-lotv/ielement-types";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { fetchProjectIElements } from "@store/i-elements/i-elements-slice-thunk";
import { AppDispatch, RootState } from "@store/store-helper";
import {
  BaseProgressApiClient,
  BaseProjectApiClientProps,
} from "@store/store-types";
import {
  generateSdbBackgroundTasks,
  getTasksIElementIds,
} from "@utils/sdb-background-tasks-utils";

interface UpdateBackgroundTasksProps
  extends BaseProjectApiClientProps,
    BaseProgressApiClient {
  /** Page size per API request. Default = ITEMS_PER_PAGE. */
  pageSize?: number;

  /** The page token to fetch the tasks before the last fetched page */
  beforePage: string | null;

  /** Whether to load all pages from API. Default = false (load 1st page only). */
  shouldFetchAllPages?: boolean;

  /** Fetch only tasks which were updated on this date or later. */
  fromDate?: ISOTimeString;

  /**
   * Whether the before page token should be overridden with a
   * new value after updating tasks, if there already is one present
   */
  shouldOverrideBeforePage?: boolean;

  /** Whether to load the related iElement for each task. Default = true. */
  shouldFetchIElements?: boolean;

  /** Optionally define a specific task type to be fetched */
  taskType?: BackgroundTaskType;
}

interface UpdateBackgroundTasksResult
  extends Pick<
      UpdateBackgroundTasksProps,
      "shouldOverrideBeforePage" | "taskType"
    >,
    Pick<ProgressApiLatestStatusResponse, "before" | "after" | "totalCount"> {
  /** List of background tasks */
  sdbBackgroundTasks: SdbBackgroundTask[];
}

/**
 * Fetches the list of background tasks from the backend so they can be put into the store
 * Also fetches the iElements related to the tasks if specified
 * @throws {Error} if the Progress API client request fails
 * @throws {ApiResponseError} if the Project API client request fails
 */
export const updateBackgroundTasks = createAsyncThunk<
  UpdateBackgroundTasksResult,
  UpdateBackgroundTasksProps,
  {
    state: RootState;
    dispatch: AppDispatch;
  }
>(
  "sdbBackgroundTasks/updateBackgroundTasks",
  async (
    {
      progressApiClient,
      projectApiClient,
      fromDate,
      pageSize,
      beforePage,
      shouldFetchAllPages = false,
      shouldOverrideBeforePage = true,
      shouldFetchIElements = true,
      taskType,
    },
    { dispatch }
  ) => {
    const { data, totalCount, before, after } =
      await progressApiClient.requestProgress({
        fromDate,
        pageSize,
        before: beforePage,
        shouldFetchAllPages,
        taskType,
      });

    if (data) {
      const iElementsIds = shouldFetchIElements
        ? getTasksIElementIds(data)
        : undefined;

      // If the iElementsIds length is zero then all project iElements will be fetched
      // This is not desired since projects can typically have thousands of iElements and fetching them
      // each minute it's demanding for the SDB and the backend.
      if (iElementsIds?.length) {
        // Fetch IElements associated with the fetched tasks
        await dispatch(
          fetchProjectIElements({
            fetcher: async () => {
              const elements = await projectApiClient.getAllIElements({
                descendantIds: iElementsIds,
              });
              return elements;
            },
          })
        ).unwrap();
      }

      const sdbBackgroundTasks = generateSdbBackgroundTasks(data);

      return {
        sdbBackgroundTasks,
        totalCount,
        before,
        after,
        shouldOverrideBeforePage,
      };
    }

    return {
      sdbBackgroundTasks: [],
      totalCount,
      before,
      after,
      shouldOverrideBeforePage,
    };
  }
);

interface FetchTasksByTypeProps extends BaseProgressApiClient {
  /** Type of the background task */
  taskType: BackgroundTaskType;
}

export interface FetchTasksByTypeResult {
  /** List of background tasks */
  sdbBackgroundTasks: SdbBackgroundTask[];

  /** Total count of tasks of this type from the backend */
  totalCount: number | null;

  /** The task type that was fetched */
  taskType: BackgroundTaskType;
}

/**
 * Fetches tasks of a specific type from the backend so they can be put into the store
 * @throws {Error} if the Progress API client request fails
 */
export const fetchTasksByType = createAsyncThunk<
  FetchTasksByTypeResult,
  FetchTasksByTypeProps
>(
  "sdbBackgroundTasks/fetchTasksByType",
  async ({ progressApiClient, taskType }) => {
    const { data, totalCount } = await progressApiClient.requestProgress({
      before: null,
      taskType,
    });

    if (!data) {
      return { sdbBackgroundTasks: [], totalCount, taskType };
    }

    return {
      sdbBackgroundTasks: generateSdbBackgroundTasks(data),
      totalCount,
      taskType,
    };
  }
);
